import React, { useState, useEffect } from "react";
import { rolesService } from "@/services/rolesService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2, Plus, X } from "lucide-react";

const RoleManagement = () => {
  const { toast } = useToast();
  const [roles, setRoles] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleDetails, setRoleDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [creatingRole, setCreatingRole] = useState(false);
  const [addingPage, setAddingPage] = useState(false);
  const [deletingRole, setDeletingRole] = useState(false);
  const [removingPage, setRemovingPage] = useState(null);
  const [roleScope, setRoleScope] = useState("all"); // "all" or "own"

  // Form states
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [newRoleViewAllData, setNewRoleViewAllData] = useState(false);
  const [selectedPagesOnCreation, setSelectedPagesOnCreation] = useState([]);
  const [selectedPageToAdd, setSelectedPageToAdd] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Fetch roles on mount and when scope changes
  useEffect(() => {
    fetchAvailablePages();
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [roleScope]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await rolesService.list(roleScope);
      // Ensure data is an array
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast({
        title: "Error",
        description: "Failed to fetch roles",
        variant: "destructive",
      });
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePages = async () => {
    try {
      const response = await rolesService.availablePages();
      setAvailablePages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch available pages:", error);
      setAvailablePages([]);
    }
  };

  const fetchRoleDetails = async (roleId) => {
    setSelectedRole(roleId);
    setLoadingDetails(true);
    try {
      const response = await rolesService.get(roleId);
      setRoleDetails(response.data);
    } catch (error) {
      console.error("Error fetching role details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch role details",
        variant: "destructive",
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) {
      toast({
        title: "Error",
        description: "Role name is required",
        variant: "destructive",
      });
      return;
    }

    setCreatingRole(true);
    try {
      const response = await rolesService.create({
        role_name: newRoleName,
        description: newRoleDescription,
        view_all_data: newRoleViewAllData,
      });
      const result = response.data;
      const roleId = result.role.role_id;

        // Assign selected pages to the new role (with sequential requests to avoid conflicts)
        if (selectedPagesOnCreation.length > 0) {
          let successCount = 0;
          let failedPages = [];

          for (const pageName of selectedPagesOnCreation) {
            try {
              await rolesService.assignPage(roleId, pageName);
              successCount++;
              console.log(`✓ Assigned ${pageName} to role`);
            } catch (err) {
              failedPages.push(pageName);
              console.error(`✗ Error assigning ${pageName}:`, err);
            }
          }

          // Show result
          if (failedPages.length === 0) {
            toast({
              title: "✓ Success",
              description: `Role "Super Admin" created with all ${successCount} page(s) assigned!`,
            });
          } else {
            toast({
              title: "⚠ Partial Success",
              description: `Role created: ${successCount}/${selectedPagesOnCreation.length} pages assigned. Issues: ${failedPages.join("; ")}`,
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "Success",
            description: "Role created successfully",
          });
        }

        setNewRoleName("");
        setNewRoleDescription("");
        setNewRoleViewAllData(false);
        setSelectedPagesOnCreation([]);
        setCreatingRole(false);
        fetchRoles();
      } catch (error) {
        const errorMsg = error.response?.data?.detail || error.message || "Failed to create role";
        toast({
          title: "Error",
          description: errorMsg,
          variant: "destructive",
        });
        setCreatingRole(false);
      }
  };

  const handleAddPage = async () => {
    if (!selectedRole || !selectedPageToAdd) return;

    setAddingPage(true);
    toast({
      title: "Adding page...",
      description: "Please wait while the page is being assigned",
    });
    try {
      await rolesService.assignPage(selectedRole, selectedPageToAdd);
      toast({
        title: "✓ Success",
        description: "Page assigned to role",
      });
      setSelectedPageToAdd("");
      fetchRoleDetails(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to assign page";
      toast({
        title: "✗ Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setAddingPage(false);
    }
  };

  const handleRemovePage = async (pageName) => {
    if (!selectedRole) return;

    setRemovingPage(pageName);
    toast({
      title: "Removing page...",
      description: "Please wait while the page is being removed",
    });
    try {
      await rolesService.removePage(selectedRole, pageName);
      toast({
        title: "✓ Success",
        description: "Page removed from role",
      });
      fetchRoleDetails(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to remove page";
      toast({
        title: "✗ Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setRemovingPage(null);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteTarget?.id) return;

    setDeletingRole(true);
    toast({
      title: "Deleting role...",
      description: "Please wait while the role is being deleted",
    });
    try {
      await rolesService.delete(deleteTarget.id);
      toast({
        title: "✓ Success",
        description: "Role deleted successfully",
      });
      fetchRoles();
      setSelectedRole(null);
      setRoleDetails(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to delete role";
      toast({
        title: "✗ Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setDeletingRole(false);
    }
  };

  const handleUpdateRoleDataAccess = async (newViewAllData) => {
    if (!selectedRole || !roleDetails?.role) return;

    try {
      await rolesService.update(selectedRole, {
        role_name: roleDetails.role.role_name,
        description: roleDetails.role.description || "",
        view_all_data: newViewAllData,
      });
      toast({
        title: "✓ Success",
        description: `Data access level updated to "${newViewAllData ? "All Data" : "Own Data"}"`,
      });
      fetchRoleDetails(selectedRole);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to update role";
      toast({
        title: "✗ Error",
        description: errorMsg,
        variant: "destructive",
      });
      // Revert the UI change
      setRoleDetails({
        ...roleDetails,
        role: {
          ...roleDetails.role,
          view_all_data: !newViewAllData,
        },
      });
    }
  };

  const getUnassignedPages = () => {
    if (!roleDetails || !Array.isArray(roleDetails.permissions)) return availablePages;
    const assignedPageNames = roleDetails.permissions.map((p) => p.page_name);
    return availablePages.filter((p) => !assignedPageNames.includes(p.name));
  };

  const togglePageSelection = (pageName) => {
    setSelectedPagesOnCreation((prev) =>
      prev.includes(pageName)
        ? prev.filter((p) => p !== pageName)
        : [...prev, pageName]
    );
  };

  return (
      <div className="min-h-screen bg-black text-white px-3 sm:px-4 md:px-8 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8 font-manrope">Role Management</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {/* Create Role Section */}
          <Card className="bg-zinc-950 border-zinc-900">
            <CardHeader>
              <CardTitle className="font-manrope">Create New Role</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateRole} className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">
                    Role Name
                  </label>
                  <Input
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    placeholder="e.g., Manager, Editor, Viewer"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">
                    Description (Optional)
                  </label>
                  <Textarea
                    value={newRoleDescription}
                    onChange={(e) => setNewRoleDescription(e.target.value)}
                    placeholder="Role description..."
                    className="bg-zinc-900 border-zinc-800 text-white min-h-20"
                  />
                </div>

                {/* View All Data Toggle */}
                <div className="bg-zinc-900 p-3 rounded-lg border border-zinc-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm text-zinc-300 font-semibold block mb-1">
                        Data Access Level
                      </label>
                      <p className="text-xs text-zinc-500">
                        {newRoleViewAllData 
                          ? "Users with this role will see ALL data across the system" 
                          : "Users with this role will only see their own data"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-zinc-400">
                        {newRoleViewAllData ? "All Data" : "Own Data"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setNewRoleViewAllData(!newRoleViewAllData)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                          newRoleViewAllData 
                            ? "bg-brand-500" 
                            : "bg-zinc-700"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                            newRoleViewAllData ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Page Selection */}
                <div>
                  <label className="text-sm text-zinc-400 mb-3 block font-semibold">
                    Select Pages (Optional)
                  </label>
                  <div className="space-y-2 bg-zinc-900 p-3 rounded-lg border border-zinc-800">
                    {availablePages.length === 0 ? (
                      <p className="text-zinc-500 text-xs">Loading pages...</p>
                    ) : (
                      availablePages.map((page) => (
                        <div key={page.name} className="flex items-center gap-2">
                          <Checkbox
                            id={`page-${page.name}`}
                            checked={selectedPagesOnCreation.includes(page.name)}
                            onCheckedChange={() => togglePageSelection(page.name)}
                            className="border-zinc-600"
                          />
                          <label
                            htmlFor={`page-${page.name}`}
                            className="text-sm text-zinc-300 cursor-pointer flex-1"
                          >
                            {page.label}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  {selectedPagesOnCreation.length > 0 && (
                    <p className="text-xs text-blue-400 mt-2">
                      {selectedPagesOnCreation.length} page(s) selected
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={creatingRole}
                  className="w-full bg-brand-500 hover:bg-brand-600 disabled:bg-brand-700 disabled:cursor-not-allowed"
                >
                  {creatingRole ? "Creating..." : "Create Role"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Roles List */}
          <Card className="bg-zinc-950 border-zinc-900">
            <CardHeader>
              <div className="flex justify-between items-center mb-4">
                <CardTitle className="font-manrope">Existing Roles</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant={roleScope === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setRoleScope("all")}
                    className={roleScope === "all" ? "bg-brand-500 hover:bg-brand-600" : ""}
                  >
                    All Roles
                  </Button>
                  <Button
                    variant={roleScope === "own" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setRoleScope("own")}
                    className={roleScope === "own" ? "bg-brand-500 hover:bg-brand-600" : ""}
                  >
                    My Roles
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full rounded-lg bg-zinc-900" />
                  ))
                ) : roles.length === 0 ? (
                  <p className="text-zinc-500 text-sm">
                    {roleScope === "own" ? "No roles created by you" : "No roles found"}
                  </p>
                ) : (
                  roles.map((role) => (
                    <div
                      key={role.role_id}
                      onClick={() => fetchRoleDetails(role.role_id)}
                      className={`p-3 rounded-lg cursor-pointer transition ${
                        selectedRole === role.role_id
                          ? "bg-brand-500 text-white"
                          : "bg-zinc-900 hover:bg-zinc-800 text-zinc-300"
                      }`}
                    >
                      <div className="font-medium">{role.role_name}</div>
                      <div className="text-xs mt-1 opacity-75">
                        {role.description || "No description"}
                      </div>
                      {role.created_by && (
                        <div className="text-xs mt-2 text-zinc-500">
                          Created by: {role.created_by}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Role Details Section */}
        {selectedRole && loadingDetails && (
          <Card className="bg-zinc-950 border-zinc-900 mt-8">
            <CardContent className="pt-6 space-y-4">
              <Skeleton className="h-6 w-48 bg-zinc-900" />
              <Skeleton className="h-4 w-full bg-zinc-900" />
              <Skeleton className="h-24 w-full bg-zinc-900" />
            </CardContent>
          </Card>
        )}
        {selectedRole && !loadingDetails && roleDetails && (
          <Card className="bg-zinc-950 border-zinc-900 mt-8">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="font-manrope">
                    {roleDetails.role.role_name}
                  </CardTitle>
                  <p className="text-sm text-zinc-400 mt-2">
                    {roleDetails.role.description || "No description"}
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setDeleteTarget(roleDetails.role);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Role
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Data Access Level Section */}
              <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-300 block mb-1">
                      Data Access Level
                    </h3>
                    <p className="text-xs text-zinc-500">
                      {roleDetails.role.view_all_data 
                        ? "Users with this role can see ALL data across the system" 
                        : "Users with this role can only see their own data"}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-zinc-400">
                      {roleDetails.role.view_all_data ? "All Data" : "Own Data"}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const newViewAllData = !roleDetails.role.view_all_data;
                        setRoleDetails({
                          ...roleDetails,
                          role: {
                            ...roleDetails.role,
                            view_all_data: newViewAllData,
                          },
                        });
                        // Call update API
                        handleUpdateRoleDataAccess(newViewAllData);
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                        roleDetails.role.view_all_data 
                          ? "bg-brand-500" 
                          : "bg-zinc-700"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          roleDetails.role.view_all_data ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Assign Page Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 font-manrope">
                  Assign Pages to Role
                </h3>
                <div className="flex gap-2 mb-4">
                  <Select
                    value={selectedPageToAdd}
                    onValueChange={setSelectedPageToAdd}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectValue placeholder="Select page to add" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {getUnassignedPages().map((page) => (
                        <SelectItem key={page.name} value={page.name}>
                          {page.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleAddPage}
                    disabled={addingPage}
                    className="bg-brand-500 hover:bg-brand-600 disabled:bg-brand-700 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {addingPage ? "Adding..." : "Add"}
                  </Button>
                </div>
              </div>

              {/* Assigned Pages */}
              <div>
                <h3 className="text-lg font-semibold mb-4 font-manrope">
                  Assigned Pages ({roleDetails.permissions?.length || 0})
                </h3>
                {!roleDetails.permissions || roleDetails.permissions.length === 0 ? (
                  <p className="text-zinc-500 text-sm">
                    No pages assigned to this role
                  </p>
                ) : (
                  <div className="space-y-2">
                    {roleDetails.permissions.map((permission) => (
                      <div
                        key={permission.permission_id}
                        className="flex items-center justify-between bg-zinc-900 p-3 rounded-lg"
                      >
                        <div>
                          <Badge
                            className="bg-brand-500 text-white"
                          >
                            {permission.page_label}
                          </Badge>
                          <p className="text-xs text-zinc-500 mt-1">
                            {permission.page_path}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={removingPage === permission.page_name}
                          onClick={() =>
                            handleRemovePage(permission.page_name)
                          }
                        >
                          {removingPage === permission.page_name ? (
                            <div className="w-4 h-4 border-2 border-zinc-400 border-t-red-500 rounded-full animate-spin"></div>
                          ) : (
                            <X className="w-4 h-4 text-red-500" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-900">
          <AlertDialogTitle>Delete Role</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the role "{deleteTarget?.role_name}
            "? This action cannot be undone.
          </AlertDialogDescription>
          <div className="flex justify-end gap-4 mt-6">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRole}
              disabled={deletingRole}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed"
            >
              {deletingRole ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoleManagement;
